import React from 'react';
import { useForm } from 'react-hook-form';
import { useQuery } from '@tanstack/react-query';
import { format, addDays, addYears } from 'date-fns';
import { moduleService } from '../../api/services/moduleService';
import type License from '../../types/domain';
import type { Customer } from '../../types/domain';
import LoadingSpinner from '../common/LoadingSpinner';
import ErrorMessage from '../common/ErrorMessage';
import { Button } from '@license-management/ui';
import ModuleGrid from '../modules/ModuleGrid';

interface LicenseFormData {
  customerId: string;
  type: 'trial' | 'enterprise';
  validFrom: string;
  validUntil: string;
  maxActivations: number;
  modules: string[];
}

interface LicenseModalProps {
  isOpen: boolean;
  onClose: () => void;
  license?: License;
  customers: Customer[];
  onSubmit: (data: LicenseFormData) => Promise<void>;
  isSubmitting: boolean;
  mode?: 'view' | 'edit' | 'create';
}


const LicenseModal: React.FC<LicenseModalProps> = ({
  isOpen,
  onClose,
  license,
  customers,
  onSubmit,
  isSubmitting,
  mode = license ? 'edit' : 'create'
}) => {
  const { register, handleSubmit, formState: { errors }, reset, watch, setValue } = useForm<LicenseFormData>({
    defaultValues: {
      customerId: license?.customerId || '',
      type: license?.type || 'trial',
      validFrom: license?.validFrom ? new Date(license.validFrom).toISOString().split('T')[0] : new Date().toISOString().split('T')[0],
      validUntil: license?.validUntil ? new Date(license.validUntil).toISOString().split('T')[0] : '',
      maxActivations: license?.maxActivations || 1,
      modules: license?.modules || []
    }
  });

  const [selectedModules, setSelectedModules] = React.useState<string[]>(license?.modules || []);

  const { data: modules = [], isLoading: isLoadingModules, error: modulesError } = useQuery({
    queryKey: ['modules'],
    queryFn: () => moduleService.getModules()
  });

  // Update form value when modules are selected
  React.useEffect(() => {
    setValue('modules', selectedModules);
  }, [selectedModules, setValue]);

  const handleModuleToggle = (moduleId: string) => {
    setSelectedModules(prev => {
      if (prev.includes(moduleId)) {
        return prev.filter(id => id !== moduleId);
      } else {
        return [...prev, moduleId];
      }
    });
  };

  // Update license duration based on type
  const licenseType = watch('type');
  React.useEffect(() => {
    const validFrom = new Date(watch('validFrom'));
    if (licenseType === 'trial') {
      const validUntil = addDays(validFrom, 30);
      setValue('validUntil', format(validUntil, 'yyyy-MM-dd'));
    } else {
      const validUntil = addYears(validFrom, 1);
      setValue('validUntil', format(validUntil, 'yyyy-MM-dd'));
    }
  }, [licenseType, setValue, watch]);

  const handleFormSubmit = async (data: LicenseFormData) => {
    try {
      await onSubmit({
        ...data,
        maxActivations: Number(data.maxActivations),
        validFrom: new Date(data.validFrom).toISOString(),
        validUntil: new Date(data.validUntil).toISOString(),
        modules: selectedModules
      });
      onClose();
    } catch (error) {
      console.error('Error submitting form:', error);
    }
  };

  React.useEffect(() => {
    if (isOpen && license) {
      reset({
        type: license.type,
        modules: license.modules || [],
        validFrom: license.validFrom ? new Date(license.validFrom).toISOString().split('T')[0] : new Date().toISOString().split('T')[0],
        validUntil: license.validUntil ? new Date(license.validUntil).toISOString().split('T')[0] : '',
        customerId: license.customerId,
        maxActivations: license.maxActivations
      });
      setSelectedModules(license.modules || []);
    }
  }, [isOpen, license, reset]);


  if (!isOpen) return null;

  if (isLoadingModules) {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center">
        <LoadingSpinner />
      </div>
    );
  }

  if (customers.length === 0) {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center">
        <div className="bg-white p-8 rounded-lg shadow-xl">
          <h3 className="text-lg font-medium text-red-600 mb-4">No Customers Found</h3>
          <p className="text-gray-500">Please create a customer before creating a license.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex min-h-screen items-center justify-center px-4">
        <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity"></div>

        <div className="relative transform overflow-hidden rounded-lg bg-white px-4 pb-4 pt-5 text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-2xl sm:p-6">
          <div className="absolute right-0 top-0 pr-4 pt-4">
            <Button
              variant="ghost"
              size="icon"
              onClick={onClose}
              className="rounded-md text-gray-400 hover:text-gray-500"
            >
              <span className="sr-only">Close</span>
              <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </Button>
          </div>

          <div className="sm:flex sm:items-start">
            <div className="w-full">
              <h3 className="text-lg font-medium leading-6 text-gray-900 mb-5">
                {mode === 'create' ? 'Create License' : mode === 'edit' ? 'Edit License' : 'View License'}
              </h3>

              <form onSubmit={handleSubmit(handleFormSubmit)} className="space-y-6">
                <div className="grid grid-cols-1 gap-y-6 gap-x-4 sm:grid-cols-6">
                  <div className="sm:col-span-6">
                    <label htmlFor="customer" className="block text-sm font-medium text-gray-700">
                      Customer
                    </label>
                    <div className="mt-1">
                      <select
                        id="customer"
                        {...register('customerId', { required: 'Customer is required' })}
                        disabled={mode === 'view'}
                        className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                      >
                        <option value="">Select a customer</option>
                        {customers.map((customer) => (
                          <option key={customer.customerId} value={customer.customerId}>
                            {customer.name}
                          </option>
                        ))}
                      </select>
                      {errors.customerId && (
                        <p className="mt-2 text-sm text-red-600">{errors.customerId.message}</p>
                      )}
                    </div>
                  </div>

                  <div className="sm:col-span-3">
                    <label htmlFor="type" className="block text-sm font-medium text-gray-700">
                      License Type
                    </label>
                    <div className="mt-1">
                      <select
                        id="type"
                        {...register('type')}
                        disabled={mode === 'view'}
                        className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                      >
                        <option value="trial">Trial (30 days)</option>
                        <option value="enterprise">Enterprise (1 year)</option>
                      </select>
                    </div>
                  </div>

                  <div className="sm:col-span-3">
                    <label htmlFor="maxActivations" className="block text-sm font-medium text-gray-700">
                      Max Activations
                    </label>
                    <div className="mt-1">
                      <input
                        type="number"
                        id="maxActivations"
                        min="1"
                        {...register('maxActivations', {
                          required: 'Max activations is required',
                          min: { value: 1, message: 'Must be at least 1' },
                          valueAsNumber: true
                        })}
                        disabled={mode === 'view'}
                        className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                      />
                      {errors.maxActivations && (
                        <p className="mt-2 text-sm text-red-600">{errors.maxActivations.message}</p>
                      )}
                    </div>
                  </div>

                  <div className="sm:col-span-3">
                    <label htmlFor="validFrom" className="block text-sm font-medium text-gray-700">
                      Valid From
                    </label>
                    <div className="mt-1">
                      <input
                        type="date"
                        id="validFrom"
                        {...register('validFrom')}
                        disabled={mode === 'view'}
                        className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                      />
                    </div>
                  </div>

                  <div className="sm:col-span-3">
                    <label htmlFor="validUntil" className="block text-sm font-medium text-gray-700">
                      Valid Until
                    </label>
                    <div className="mt-1">
                      <input
                        type="date"
                        id="validUntil"
                        {...register('validUntil')}
                        disabled={mode === 'view'}
                        className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                      />
                    </div>
                  </div>
                </div>

                <div>
                  <h4 className="text-base font-medium text-gray-900 mb-4">Modules</h4>
                  {isLoadingModules ? (
                    <LoadingSpinner />
                  ) : modulesError ? (
                    <ErrorMessage message="Failed to load modules" />
                  ) : (
                    <ModuleGrid
                      modules={modules || []}
                      selectedModules={selectedModules}
                      onModuleToggle={handleModuleToggle}
                    />
                  )}
                </div>

                <div className="mt-5 sm:mt-4 sm:flex sm:flex-row-reverse">
                  {mode !== 'view' && (
                    <Button
                      type="submit"
                      loading={isSubmitting}
                      className="sm:ml-3"
                    >
                      {mode === 'create' ? 'Create License' : 'Update License'}
                    </Button>
                  )}
                  <Button
                    type="button"
                    variant="secondary"
                    onClick={onClose}
                    className="mt-3 sm:mt-0"
                  >
                    {mode === 'view' ? 'Close' : 'Cancel'}
                  </Button>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LicenseModal;
